import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { FormString } from '..';
import { SecureButton } from '../secure_button';
import type { SCSelectProps } from '../select';
//import { SCSelect } from '../select';
import { FormSelect } from 'eone-ui/forms/form-select';
import { SCView } from '../view';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';
import { handleField } from './helper';
//import './styles.css';
import './grid.css';

type SCFormFieldSCSelectProps<V extends string | number = string> = SCFormFieldProps &
  SCSelectProps<V> & {
    MultiSelect?: boolean;
    inline?: boolean;
    /**
     * Render the select as a non-interactive, read-only value. Unlike `disabled` (which the
     * underlying eone-ui `FormSelect`/`Select` silently ignores), this is the prop the base select
     * actually honors — mirroring how the sibling `SCSelect` maps `disabled` onto `ReadOnly`.
     */
    ReadOnly?: boolean;
  };

const SCFormFieldSCSelect = <V extends string | number = string>({
  label,
  name,
  required,
  disabled,
  rules,
  visible,
  defaultValue,
  MultiSelect,
  secure,
  inline,
  options,
  loading,
  ReadOnly,
  ...rest
}: SCFormFieldSCSelectProps<V>) => {
  const [isSecure, setIsSecure] = useState(secure ?? false);
  const hasLabel = !isEmpty(label);
  const showRequiredIndicator = hasLabel && (required || !!rules?.required);
  const style = useMemo(() => {
    if (inline) return { display: 'flex', alignItems: 'center', gap: 10 };
    return {};
  }, [inline]);

  return (
    <SCFormField
      visible={visible}
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      rules={rules}
      defaultValue={defaultValue}
      //withMarginBottom={}
    >
      {({ field, extraProps }) => {
        const fieldProps = { ...handleField(field, { disabledRef: true }) };
        const onChange = (newValue) => {
          rest.onChange?.(newValue);
          fieldProps.onChange(newValue);
        };
        const multipleWithBackwardCompat = rest.multiple || MultiSelect;
        return (
          <div style={style}>
            {/*{label && inline && <p style={{ margin: 0, whiteSpace: 'nowrap' }}>{label}</p>}*/}
            {/*{label && !inline && (*/}
            {/*  <SCFormLabel label={label} required={required || !!rules?.required} />*/}
            {/*)}*/}
            <FormSelect
              Disabled={disabled}
              ReadOnly={ReadOnly}
              {...fieldProps}
              {...rest}
              {...extraProps}
              Options={(options ?? []).map((option) => ({
                Key: option.value,
                Value: option.label,
              }))}
              Search={rest.search == undefined ? true : rest.search}
              //NOTE: Defaulting search == true if nothing is passed in
              MultiSelect={multipleWithBackwardCompat}
              Secure={isSecure}
              Value={fieldProps.value}
              OnChange={onChange}
              Label={hasLabel ? label : ''}
              Required={showRequiredIndicator}
              ShowRequiredIndicator={showRequiredIndicator}
              Loading={loading}
            />
            {/*<SCSelect*/}
            {/*	{...fieldProps}*/}
            {/*	{...rest}*/}
            {/*	{...extraProps}*/}
            {/*	search={rest.search == undefined ? true : rest.search}*/}
            {/*	//NOTE: Defaulting search == true if nothing is passed in*/}
            {/*	multiple={multipleWithBackwardCompat}*/}
            {/*	secure={isSecure}*/}
            {/*	value={fieldProps.value}*/}
            {/*	onChange={onChange}*/}
            {/*/>*/}
            {secure && <SecureButton defaultSecure onChange={setIsSecure} />}
          </div>
        );
      }}
    </SCFormField>
  );
};

export { SCFormFieldSCSelect };
export type { SCFormFieldSCSelectProps };

const DEFAULT_GRID_COLUMNS = 2;

type SCFormFieldSelectGridProps<V extends string | number = string> = SCFormFieldProps &
  SCSelectProps<V> & {
    MultiSelect?: boolean;
    Options: { value: string; label: string; subLabel: string; imageSrc: string }[];
    Columns?: number;
  };

function SCFormFieldSelectGrid({
  label,
  name,
  required,
  disabled,
  rules,
  visible,
  defaultValue,
  MultiSelect,
  Options,
  Columns,
  loading,
  ...rest
}: SCFormFieldSelectGridProps) {
  const [displayedOptions, setDisplayedOptions] = useState<
    { value: string; label: string; subLabel: string; imageSrc: string }[]
  >([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const opts = Array.isArray(Options) ? Options : [];
    if (isEmpty(search)) {
      setDisplayedOptions(opts);
    } else {
      const searchQuery = search.toLowerCase();
      const newDisplayedOptions = opts.filter((option) =>
        option.label.toLowerCase().includes(searchQuery),
      );
      setDisplayedOptions(newDisplayedOptions);
    }
  }, [Options, search]);

  if (loading) {
    return (
      <div className="sc-grid-container">
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            height: 212,
          }}
        >
          <FontAwesomeIcon icon={faSpinnerThird} spin />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SCFormField
      visible={visible}
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      rules={rules}
      defaultValue={defaultValue}
      withMarginBottom
    >
      {({ field }) => {
        const fieldProps = { ...handleField(field, { disabledRef: true }) };
        const onChange = (newValue) => {
          let newVal = newValue;

          if (MultiSelect) {
            const fieldValue = field?.value || '';

            if (fieldValue.includes(newValue)) {
              const isAtStart = fieldValue.indexOf(newValue) == 0;
              if (isAtStart && fieldValue.length == newValue.length) {
                newVal = fieldValue.replace(newValue, '');
              } else if (isAtStart && fieldValue.length > newValue.length) {
                newVal = fieldValue.replace(`${newValue},`, '');
              } else {
                newVal = fieldValue.replace(`,${newValue}`, '');
              }
            } else {
              if (fieldValue.length > 0) {
                newVal = fieldValue + ',' + newValue;
              }
            }
          } else {
            if (field?.value == newValue) {
              newVal = '';
            }
          }
          rest.onChange?.(newVal);
          fieldProps.onChange(newVal);
        };

        return (
          <div className="sc-grid-container">
            <FormString
              Label=""
              Value={search}
              OnChange={(value) => setSearch(value)}
              Placeholder="Search..."
              Icon={faSearch}
            />
            {!loading && (
              <SCView flex flexRow wFull>
                <SCView flex flex1>
                  <SCView wFull>
                    {displayedOptions?.length == 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <p>
                          {isEmpty(search)
                            ? 'No options available'
                            : 'No found matching options'}{' '}
                        </p>
                      </div>
                    )}

                    {displayedOptions && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${Columns || DEFAULT_GRID_COLUMNS}, 1fr)`,
                          gap: 10,
                        }}
                      >
                        {displayedOptions.map((item) => {
                          const selected = MultiSelect
                            ? field.value?.includes(item.value)
                            : field.value == item.value;

                          return (
                            <div
                              onClick={() => onChange(item.value)}
                              className={cn('sc-grid-item', selected && 'selected')}
                              key={item.value}
                            >
                              {item.imageSrc && (
                                <img className="sc-grid-item-logo" src={item.imageSrc} />
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span className="sc-grid-item-title">{item.label}</span>
                                <span className="sc-grid-item-sub-label">{item.subLabel}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </SCView>
                </SCView>
              </SCView>
            )}
          </div>
        );
      }}
    </SCFormField>
  );
}

export { SCFormFieldSelectGrid };
export type { SCFormFieldSelectGridProps };
